import { createContext, useState } from 'react'
import { Roles } from '../../constants'

export interface IMetadataInterface {
  id: string
  code: string
  name: string
  data: dataModel
}

export interface dataModel {
  id: string
  name: string
}

export interface IMetadataBaseInterface {
  key: string
  values: IMetadataInterface[]
}

export type IGenderInterface = IMetadataInterface
export type IFlowPreferenceInterface = IMetadataInterface
export type IReferralReasonInterface = IMetadataInterface
export type IMeetingPreferenceInterface = IMetadataInterface
export type IJobRoleInterface = IMetadataInterface
export type ILocationInterface = IMetadataInterface
export type ILanguageInterface = IMetadataInterface
export type IDayTypeInterface = IMetadataInterface
export type ISlotTypeInterface = IMetadataInterface
export type IRequestStatusInterface = IMetadataInterface
export type IPeerSupporterStatusInterface = IMetadataInterface
export type IMatchedPeerSupporterStatusInterface = IMetadataInterface
export type IFetchRequestStatusInterface = IMetadataInterface
export type IStateInterface = IMetadataInterface
export type IMinistryInterface = IMetadataInterface
export type INominationStatusInterface = IMetadataInterface

interface IMetadataContextInterface {
  genders: IGenderInterface[]
  flowPreferences: IFlowPreferenceInterface[]
  referralReasons: IReferralReasonInterface[]
  meetingPreferences: IMeetingPreferenceInterface[]
  jobRoles: IJobRoleInterface[]
  locations: ILocationInterface[]
  languages: ILanguageInterface[]
  dayTypes: IDayTypeInterface[]
  slotTypes: ISlotTypeInterface[]
  requestStatuses: IRequestStatusInterface[]
  peerSupporterStatuses: IPeerSupporterStatusInterface[]
  matchedSupporterStatuses: IMatchedPeerSupporterStatusInterface[]
  fetchRequestStatus: IFetchRequestStatusInterface[]
  userRole: Roles
  availabilityStatus: string
  state: IStateInterface[]
  ministrySetting: IMinistryInterface[]
  nominationStatus: INominationStatusInterface[]
  setMinistrySettings: React.Dispatch<React.SetStateAction<IMinistryInterface[]>>
  setState: React.Dispatch<React.SetStateAction<IStateInterface[]>>
  setGenders: React.Dispatch<React.SetStateAction<IGenderInterface[]>>
  setFlowPreferences: React.Dispatch<
    React.SetStateAction<IFlowPreferenceInterface[]>
  >
  setReferralReasons: React.Dispatch<
    React.SetStateAction<IReferralReasonInterface[]>
  >
  setMeetingPreferences: React.Dispatch<
    React.SetStateAction<IMeetingPreferenceInterface[]>
  >
  setJobRoles: React.Dispatch<React.SetStateAction<IJobRoleInterface[]>>
  setLocations: React.Dispatch<React.SetStateAction<ILocationInterface[]>>
  setLanguages: React.Dispatch<React.SetStateAction<ILanguageInterface[]>>
  setDayTypes: React.Dispatch<React.SetStateAction<IDayTypeInterface[]>>
  setSlotTypes: React.Dispatch<React.SetStateAction<ISlotTypeInterface[]>>
  setRequestStatuses: React.Dispatch<
    React.SetStateAction<IRequestStatusInterface[]>
  >
  setPeerSupporterStatuses: React.Dispatch<
    React.SetStateAction<IPeerSupporterStatusInterface[]>
  >
  setMatchedSupporterStatuses: React.Dispatch<
    React.SetStateAction<IMatchedPeerSupporterStatusInterface[]>
  >
  setFetchRequestStatuses: React.Dispatch<
    React.SetStateAction<IFetchRequestStatusInterface[]>
  >
  setUserRole: React.Dispatch<React.SetStateAction<Roles>>
  setAvailabilityStatus: React.Dispatch<React.SetStateAction<string>>
  setNominationStatus: React.Dispatch<React.SetStateAction<INominationStatusInterface[]>>
}

export const MetadataContext = createContext({} as IMetadataContextInterface)

const MetadataMiddleware: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [genders, setGenders] = useState<IGenderInterface[]>([])
  const [languages, setLanguages] = useState<ILanguageInterface[]>([])
  const [locations, setLocations] = useState<ILocationInterface[]>([])
  const [jobRoles, setJobRoles] = useState<IJobRoleInterface[]>([])
  const [meetingPreferences, setMeetingPreferences] = useState<
    IMeetingPreferenceInterface[]
  >([])
  const [referralReasons, setReferralReasons] = useState<
    IReferralReasonInterface[]
  >([])
  const [flowPreferences, setFlowPreferences] = useState<
    IFlowPreferenceInterface[]
  >([])
  const [dayTypes, setDayTypes] = useState<IDayTypeInterface[]>([])
  const [slotTypes, setSlotTypes] = useState<ISlotTypeInterface[]>([])
  const [requestStatuses, setRequestStatuses] = useState<
    IRequestStatusInterface[]
  >([])
  const [peerSupporterStatuses, setPeerSupporterStatuses] = useState<
    IPeerSupporterStatusInterface[]
  >([])
  const [matchedSupporterStatuses, setMatchedSupporterStatuses] = useState<
    IMatchedPeerSupporterStatusInterface[]
  >([])
  const [fetchRequestStatus, setFetchRequestStatuses] = useState<
    IFetchRequestStatusInterface[]
  >([])
  const [userRole, setUserRole] = useState<Roles>(Roles.USER)
  const [availabilityStatus, setAvailabilityStatus] =
    useState<string>('Available')
  const [state, setState] = useState<IStateInterface[]>([])
  const [ministrySetting, setMinistrySettings] = useState<IMinistryInterface[]>([])
  const [nominationStatus, setNominationStatus] = useState<INominationStatusInterface[]>([])

  return (
    <MetadataContext.Provider
      value={{
        state,
        genders,
        flowPreferences,
        referralReasons,
        meetingPreferences,
        jobRoles,
        locations,
        languages,
        dayTypes,
        slotTypes,
        requestStatuses,
        peerSupporterStatuses,
        matchedSupporterStatuses,
        fetchRequestStatus,
        userRole,
        availabilityStatus,
        ministrySetting,
        nominationStatus,
        setNominationStatus,
        setMinistrySettings,
        setState,
        setGenders,
        setFlowPreferences,
        setReferralReasons,
        setMeetingPreferences,
        setJobRoles,
        setLocations,
        setLanguages,
        setDayTypes,
        setSlotTypes,
        setRequestStatuses,
        setPeerSupporterStatuses,
        setMatchedSupporterStatuses,
        setFetchRequestStatuses,
        setUserRole,
        setAvailabilityStatus,
      }}
    >
      {children}
    </MetadataContext.Provider>
  )
}

export default MetadataMiddleware
